import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from "react-router-dom";

import { UserProvider } from "@/contexts/UserContext";
import { AdminViewProvider } from "@/contexts/AdminViewContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import Index from "./pages/Index";
import FeedPage from "./pages/FeedPage";
import MarketplacePage from "./pages/MarketplacePage";
import BrandDashboardPage from "./pages/BrandDashboardPage";
import VentureDashboardPage from "./pages/VentureDashboardPage";
import DealsPartnershipsPage from "./pages/DealsPartnershipsPage";
import DealDetailPage from "./pages/DealDetailPage";
import ProfilePage from "./pages/ProfilePage";
import TalentMarketplacePage from "./pages/TalentMarketplacePage";
import AnalyticsVaultPage from "./pages/AnalyticsVaultPage";
import AuthPage from "./pages/AuthPage";
import BetaSignupPage from "./pages/BetaSignupPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetupPasswordPage from "./pages/SetupPasswordPage";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import OnboardingPage from "./pages/OnboardingPage";
import ProfileCompletionPage from "./pages/ProfileCompletionPage";
import DealProgressPage from "./pages/DealProgressPage";
import SettingsPage from "./pages/SettingsPage";
import SliceAIPage from "./pages/SliceAIPage";
import HoldingsPage from "./pages/HoldingsPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import WalletPage from "./pages/WalletPage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import PreLaunchChecklistPage from "./pages/PreLaunchChecklistPage";
import APISportsManagementPage from "./pages/APISportsManagementPage";
import ClaimAthleteProfilePage from "./pages/ClaimAthleteProfilePage";
import ClaimCreatorProfilePage from "./pages/ClaimCreatorProfilePage";
import HelpPage from "./pages/HelpPage";
import CreateDealPage from "./pages/CreateDealPage";

import AdminImportAccountsPage from "./pages/AdminImportAccountsPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import ScrollToTop from "./components/ScrollToTop";

import NotFound from "./pages/NotFound";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import AdminBetaApplicationsPage from "./pages/AdminBetaApplicationsPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerificationExpiredPage from "./pages/VerificationExpiredPage";

const queryClient = new QueryClient();

// Component to detect recovery/signup hash and redirect BEFORE UserProvider blocks - runs outside UserProvider
function RecoveryRedirector() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    
    const params = new URLSearchParams(hash.substring(1));
    const type = params.get('type');
    
    // Handle password recovery
    if (type === 'recovery' && window.location.pathname !== '/reset-password') {
      console.log('RecoveryRedirector: Detected recovery mode via hash, redirecting to /reset-password');
      navigate('/reset-password' + hash, { replace: true });
      return;
    }
    
    // Handle invitation acceptance or magiclink (claim account)
    if ((type === 'invite' || type === 'magiclink') && window.location.pathname !== '/setup-password') {
      console.log('RecoveryRedirector: Detected invite/magiclink mode, redirecting to /setup-password');
      navigate('/setup-password' + hash, { replace: true });
      return;
    }
    
    // Handle signup verification - redirect to role-selection IMMEDIATELY
    if (type === 'signup' && window.location.pathname !== '/role-selection') {
      console.log('RecoveryRedirector: Detected signup verification, setting flag and redirecting to /role-selection');
      sessionStorage.setItem('isSignupVerification', 'true');
      localStorage.removeItem('accountType');
      localStorage.removeItem('currentUser');
      navigate('/role-selection', { replace: true });
      return;
    }
  }, [navigate]);
  
  return null;
}

// Component to catch auth errors IMMEDIATELY before ProtectedRoute interferes
// Synchronous redirect component — no useEffect flash
function RootRedirect() {
  const hash = window.location.hash;
  if (hash) {
    const params = new URLSearchParams(hash.substring(1));
    const type = params.get('type');
    if (type === 'recovery') return <Navigate to={'/reset-password' + hash} replace />;
    if (type === 'magiclink' || type === 'invite') return <Navigate to={'/setup-password' + hash} replace />;
    if (type === 'signup') return <Navigate to="/role-selection" replace />;
  }
  return <Navigate to="/auth" replace />;
}

function AuthErrorHandler() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check URL params for errors
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorCode = urlParams.get('error_code');
    const errorDescription = (urlParams.get('error_description') || '').toLowerCase();

    // Check hash params for errors (Supabase sometimes puts errors here)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hashError = hashParams.get('error');
    const hashErrorCode = hashParams.get('error_code');
    const hashErrorDescription = (hashParams.get('error_description') || '').toLowerCase();

    // Comprehensive error detection for expired/invalid verification links
    const isAuthError = 
      errorCode === 'otp_expired' ||
      hashErrorCode === 'otp_expired' ||
      error === 'access_denied' ||
      hashError === 'access_denied' ||
      errorDescription.includes('expired') ||
      errorDescription.includes('invalid') ||
      errorDescription.includes('token not found') ||
      errorDescription.includes('otp') ||
      hashErrorDescription.includes('expired') ||
      hashErrorDescription.includes('invalid') ||
      hashErrorDescription.includes('token not found') ||
      hashErrorDescription.includes('otp');

    if (isAuthError) {
      console.log('AuthErrorHandler: Detected auth error, redirecting to verification-expired', {
        error, errorCode, errorDescription, hashError, hashErrorCode, hashErrorDescription
      });
      navigate('/verification-expired', { replace: true });
    }
  }, [navigate]);
  
  return null;
}


// Wrapper that provides UserProvider + AdminViewProvider for authenticated routes
function AuthenticatedRoutes() {
  return (
    <UserProvider>
      <AdminViewProvider>
        <Routes>
          {/* Setup routes */}
          <Route path="/pending-approval" element={<ProtectedRoute><PendingApprovalPage /></ProtectedRoute>} />
          <Route path="/role-selection" element={<RoleSelectionPage />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          
          {/* Protected routes - root handled by RootRedirect */}
          <Route path="/slice-ai" element={<ProtectedRoute><SliceAIPage /></ProtectedRoute>} />
          <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
          <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
          <Route path="/venture-dashboard" element={<ProtectedRoute><VentureDashboardPage /></ProtectedRoute>} />
          <Route path="/brand-dashboard" element={<ProtectedRoute><BrandDashboardPage /></ProtectedRoute>} />
          <Route path="/deals-partnerships" element={<ProtectedRoute><DealsPartnershipsPage /></ProtectedRoute>} />
          <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetailPage /></ProtectedRoute>} />
          <Route path="/talent-marketplace" element={<ProtectedRoute><TalentMarketplacePage /></ProtectedRoute>} />
          <Route path="/analytics-vault" element={<ProtectedRoute><AnalyticsVaultPage /></ProtectedRoute>} />
          <Route path="/deal-progress" element={<ProtectedRoute><DealProgressPage /></ProtectedRoute>} />
          <Route path="/holdings" element={<ProtectedRoute><HoldingsPage /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
          <Route path="/transaction-history" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/complete" element={<ProtectedRoute><ProfileCompletionPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/mentions" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
          <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedbackPage /></ProtectedRoute>} />
          <Route path="/admin/checklist" element={<ProtectedRoute><PreLaunchChecklistPage /></ProtectedRoute>} />
          <Route path="/admin/beta-applications" element={<ProtectedRoute><AdminBetaApplicationsPage /></ProtectedRoute>} />
          <Route path="/admin/api-sports" element={<ProtectedRoute><APISportsManagementPage /></ProtectedRoute>} />
          <Route path="/admin/import-accounts" element={<ProtectedRoute><AdminImportAccountsPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/claim-athlete" element={<ProtectedRoute><ClaimAthleteProfilePage /></ProtectedRoute>} />
          <Route path="/claim-creator" element={<ProtectedRoute><ClaimCreatorProfilePage /></ProtectedRoute>} />
          <Route path="/create-deal" element={<ProtectedRoute><CreateDealPage /></ProtectedRoute>} />
          
          <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
          
          {/* Legacy route */}
          <Route path="/new-user" element={<AuthPage />} />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AdminViewProvider>
    </UserProvider>
  );
}

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <RecoveryRedirector />
              <AuthErrorHandler />
              <Routes>
                {/* Public routes — render immediately, no UserProvider blocking */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/setup-password" element={<SetupPasswordPage />} />
                <Route path="/beta-signup" element={<BetaSignupPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/verification-expired" element={<VerificationExpiredPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/auth" element={<AuthPage />} />
                
                {/* All other routes go through UserProvider */}
                <Route path="*" element={<AuthenticatedRoutes />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
